import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import type { Sitting, TranscriptLine, TranscriptPack } from '../../lib/contract'
import styles from './Transcripts.module.css'

/**
 * READING SITTINGS BACK — the feedback moat's instrument (2026-08-29).
 *
 * ⚠ NOTHING WAS EVER MISSING FROM THE DATABASE. Every line of every sitting has always been in
 * `session.state->'Messages'`, and nothing trims it. This page is a door onto data that was already
 * complete, which is why there is no "logging" anywhere behind it.
 *
 * ⚠ AND IT CANNOT SHOW WHAT THE SERVER WILL NOT SEND. The transcript posture
 * (`the-everyday-rehearsal.md` §5.5 duty 2 — *"architecture, not policy text"*) is enforced inside
 * the query: an `unread` pack's sittings are absent from the list and 404 on read.
 *
 * ⚠ THE DECISION IS MADE HERE; IT IS NOT ENFORCED HERE, and the distinction is the whole safety of
 * the screen. The posture section below WRITES a pack's posture — that is deliberate, because a
 * seam whose only control is hand-written SQL is one nobody adjusts and everybody works around.
 * But this component never decides what it may *display*: it asks the server again after every
 * change and renders whatever comes back. So a bug here can show the wrong BUTTON; it cannot show
 * a withheld conversation.
 *
 * The posture section lists every pack, read and unread alike, with the action each one affords. A
 * privacy decision nobody can see is one nobody can correct, so the walls are always on screen —
 * and a pack that is unread merely because nobody classified it gets noticed rather than quietly
 * never reviewed.
 *
 * Ships in the production bundle and is worth nothing on its own — every endpoint is admin-checked
 * server-side (`/api/admin/*` classifies as `AccessTier.Admin`).
 */
export default function Transcripts() {
  const [packs, setPacks] = useState<TranscriptPack[] | null>(null)
  const [sittings, setSittings] = useState<Sitting[] | null>(null)
  const [pack, setPack] = useState<string | null>(null)
  const [open, setOpen] = useState<{ sitting: Sitting; lines: TranscriptLine[] } | null>(null)
  const [reading, setReading] = useState<string | null>(null)
  const [error, setError] = useState('')
  /** The reason typed against each unread pack. Required to open one; see `decide`. */
  const [reason, setReason] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const [p, s] = await Promise.all([api.transcriptPacks(), api.listSittings()])
        setPacks(p.packs)
        setSittings(s.sittings)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not read the sittings.')
        setPacks([])
        setSittings([])
      }
    })()
  }, [])

  const choose = useCallback(async (packId: string | null) => {
    setPack(packId)
    setOpen(null)
    setSittings(null)
    try {
      setSittings((await api.listSittings(packId ?? undefined)).sittings)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the sittings.')
      setSittings([])
    }
  }, [])

  /**
   * Decide a pack's posture.
   *
   * ⚠ THE SITTINGS LIST IS RELOADED, NOT PATCHED. Opening a pack adds every sitting it has ever
   * produced to what this page can show, and closing one removes them; guessing at that locally
   * would mean the screen and the server disagreeing about who can be read, which is the one thing
   * this page must never do. The server is asked again.
   */
  async function decide(packId: string, posture: 'readable' | 'unread', note?: string) {
    setSaving(packId)
    setError('')
    try {
      await api.setPosture(packId, posture, note)
      const [p, s] = await Promise.all([
        api.transcriptPacks(),
        api.listSittings(pack ?? undefined),
      ])
      setPacks(p.packs)
      setSittings(s.sittings)
      setReason((r) => ({ ...r, [packId]: '' }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That posture could not be set.')
    } finally {
      setSaving(null)
    }
  }

  async function read(s: Sitting) {
    setReading(s.sessionId)
    setError('')
    try {
      setOpen(await api.readSitting(s.sessionId))
    } catch (e) {
      // A 404 here is the posture doing its job, or a sitting that has gone. Both are the same
      // sentence to the reader, which is the point of the server answering them identically.
      setError(e instanceof Error ? e.message : 'That sitting could not be read.')
    } finally {
      setReading(null)
    }
  }

  const readable = packs?.filter((p) => p.posture === 'readable').length ?? 0

  if (open) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.head}>
            <h1 className={styles.title}>{open.sitting.packName || open.sitting.packId}</h1>
            <button className={styles.back} onClick={() => setOpen(null)}>
              ‹ ALL SITTINGS
            </button>
          </div>

          <p className={styles.count}>
            {open.sitting.email ?? 'no account'}
            {open.sitting.personaId ? ` · ${open.sitting.personaId}` : ''} ·{' '}
            {new Date(open.sitting.savedAt).toLocaleString()} · {open.sitting.turnCount} turns ·{' '}
            {/* Radical cost transparency, as a column — the same posture the statement takes. */}
            ${open.sitting.costUsd.toFixed(4)}
          </p>

          <div className={styles.thread}>
            {open.lines.length === 0 ? (
              <p className={styles.empty}>Nothing was said in this one.</p>
            ) : (
              open.lines.map((l) => (
                <div
                  key={l.ordinal}
                  className={`${styles.line} ${l.who === 'Player' ? styles.player : ''}`}
                >
                  <div className={styles.who}>{l.who === 'Player' ? 'guest' : l.label || l.who}</div>
                  {/* `white-space: pre-wrap` in the stylesheet: the engine's own line breaks are
                      part of what is being reviewed, so they are preserved rather than collapsed. */}
                  <div className={styles.said}>{l.content}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.head}>
          <h1 className={styles.title}>Sittings</h1>
          <Link to="/" className={styles.back}>
            ‹ BACK
          </Link>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.packs}>
          <button
            className={`${styles.pack} ${pack === null ? styles.on : ''}`}
            onClick={() => void choose(null)}
          >
            everything
          </button>
          {packs
            ?.filter((p) => p.posture === 'readable')
            .map((p) => (
              <button
                key={p.packId}
                className={`${styles.pack} ${pack === p.packId ? styles.on : ''}`}
                onClick={() => void choose(p.packId)}
              >
                {p.packId} <span className={styles.n}>{p.sittings}</span>
              </button>
            ))}
        </div>

        {/*
          ⚠ THE WALLS, NAMED — AND OPENABLE, with a reason. Withheld packs are listed here (not
          their sittings, only that they exist and are not being read), which is the difference
          between a promise about privacy and a visible one, and is also how a pack that is unread
          BY ACCIDENT — nobody classified it — gets noticed instead of quietly never being reviewed.

          ⚠ THE REASON FIELD IS NOT CEREMONY. `readable` is refused server-side without one, and the
          text is stored on the row, so the question "why was this ever readable" has an answer
          later. Closing a door asks for nothing — friction belongs on the direction that exposes.
        */}
        {packs && packs.length > 0 && (
          <div className={styles.withheld}>
            <p className={styles.wlead}>
              posture
              <span className={styles.wnote}>
                {' '}— unread is the default for any pack nobody has decided about
              </span>
            </p>
            {packs.map((p) => {
              const readableNow = p.posture === 'readable'
              return (
                <div key={p.packId} className={styles.wrow}>
                  <span className={readableNow ? styles.wopen : styles.wname}>{p.packId}</span>
                  <span className={styles.n}>{p.sittings}</span>
                  {readableNow ? (
                    <>
                      <span className={styles.spacer} />
                      <button
                        className={styles.close}
                        disabled={saving === p.packId}
                        onClick={() => void decide(p.packId, 'unread')}
                      >
                        stop reading
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        className={styles.reason}
                        placeholder="why these may be read…"
                        value={reason[p.packId] ?? ''}
                        onChange={(e) =>
                          setReason((r) => ({ ...r, [p.packId]: e.target.value }))
                        }
                      />
                      <button
                        className={styles.open}
                        disabled={saving === p.packId || !(reason[p.packId] ?? '').trim()}
                        onClick={() => void decide(p.packId, 'readable', reason[p.packId])}
                      >
                        make readable
                      </button>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {sittings === null ? (
          <p className={styles.empty}>reading…</p>
        ) : sittings.length === 0 ? (
          <p className={styles.empty}>
            {readable === 0
              ? 'No pack is marked readable, so there is nothing to review.'
              : 'No sittings here yet.'}
          </p>
        ) : (
          <>
            <p className={styles.count}>
              {sittings.length} sitting{sittings.length === 1 ? '' : 's'}
              {pack ? ` in ${pack}` : ''}
            </p>
            {sittings.map((s) => (
              <button
                key={s.sessionId}
                className={styles.row}
                disabled={reading === s.sessionId}
                onClick={() => void read(s)}
              >
                <div className={styles.rowWho}>
                  <div className={styles.email}>{s.email ?? 'no account'}</div>
                  <div className={styles.when}>
                    {s.packId}
                    {s.personaId ? ` · ${s.personaId}` : ''} ·{' '}
                    {new Date(s.savedAt).toLocaleString()}
                  </div>
                </div>
                <div className={styles.turns}>
                  {s.turnCount} turn{s.turnCount === 1 ? '' : 's'}
                  {s.terminated && <span className={styles.ended}> · ended</span>}
                </div>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
